import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, MessageSquare, Users, Building2, 
  AlertCircle, Clock, CheckCircle
} from 'lucide-react';

const DashboardTab = ({ authAxios }) => {
  const [stats, setStats] = useState({ total: 0, new: 0, pending: 0, resolved: 0 });
  const [submissions, setSubmissions] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({ name: '', email: '', phone: '' });
  const [teamCount, setTeamCount] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subRes, settingsRes, teamRes] = await Promise.all([
          authAxios.get('/contact'),
          authAxios.get('/admin/settings'),
          authAxios.get('/admin/team')
        ]);
        const subs = subRes.data;
        setSubmissions(subs);
        setStats({
          total: subs.length,
          new: subs.filter(s => s.status === 'new').length,
          pending: subs.filter(s => s.status === 'pending').length,
          resolved: subs.filter(s => s.status === 'resolved').length
        });
        const s = settingsRes.data;
        setCompanyInfo({
          name: s.company_info?.name || '',
          email: s.contact_details?.email || '',
          phone: s.contact_details?.phone || ''
        });
        setTeamCount(teamRes.data.length);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      }
    };
    fetchData();
  }, [authAxios]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return 'bg-blue-500/20 text-blue-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'resolved': return 'bg-green-500/20 text-green-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const statCards = [
    { label: 'Total Enquiries', value: stats.total, icon: MessageSquare, color: 'white' },
    { label: 'New', value: stats.new, icon: AlertCircle, color: 'blue' },
    { label: 'Pending', value: stats.pending, icon: Clock, color: 'yellow' },
    { label: 'Resolved', value: stats.resolved, icon: CheckCircle, color: 'green' },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-tab">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {statCards.map((stat, i) => (
          <motion.div
            key={stat.label}
            className="bg-[#111111] border border-white/5 p-6 hover:border-[#D4AF37]/30 transition-colors"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <stat.icon className={`w-5 h-5 mb-4 ${
              stat.color === 'blue' ? 'text-blue-400' :
              stat.color === 'yellow' ? 'text-yellow-400' :
              stat.color === 'green' ? 'text-green-400' : 'text-white/60'
            }`} />
            <p className="font-mono text-3xl font-bold text-white">{stat.value}</p>
            <p className="text-white/50 text-sm mt-1">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-[#111111] border border-white/5 p-6">
          <h3 className="font-mono font-bold mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#D4AF37]" />
            Quick Info
          </h3>
          <div className="space-y-3 text-sm">
            <p className="text-white/70"><span className="text-white/40">Company:</span> {companyInfo.name}</p>
            <p className="text-white/70"><span className="text-white/40">Email:</span> {companyInfo.email}</p>
            <p className="text-white/70"><span className="text-white/40">Phone:</span> {companyInfo.phone}</p>
          </div>
        </div>

        <div className="bg-[#111111] border border-white/5 p-6">
          <h3 className="font-mono font-bold mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-[#D4AF37]" />
            Team Members
          </h3>
          <p className="text-3xl font-mono font-bold text-white">{teamCount}</p>
          <p className="text-white/50 text-sm">Active members</p>
        </div>
      </div>

      <div className="bg-[#111111] border border-white/5">
        <div className="px-6 py-4 border-b border-white/5">
          <h2 className="font-mono font-bold">Recent Enquiries</h2>
        </div>
        <div className="divide-y divide-white/5">
          {submissions.slice(0, 5).map(sub => (
            <div key={sub.id} className="px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-colors">
              <div>
                <p className="font-mono text-sm font-bold">{sub.name}</p>
                <p className="text-white/50 text-xs">{sub.email}</p>
              </div>
              <div className="text-right">
                <span className={`inline-block px-2 py-1 rounded text-xs font-mono ${getStatusColor(sub.status)}`}>
                  {sub.status}
                </span>
                <p className="text-white/30 text-xs mt-1">{formatDate(sub.created_at)}</p>
              </div>
            </div>
          ))}
          {submissions.length === 0 && (
            <div className="px-6 py-8 text-center text-white/30 font-mono text-sm">No enquiries yet</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
